import classNames from 'classnames'
import { VFC } from 'react'
import { useTheme } from '@opallabs/hooks'
import { Icon } from '../Icon'
import { DateRemover } from './DateRemover'
import { Month } from './Month'
import * as styles from './styles'
import { type CalendarProps } from './types'
import { CalendarProvider } from './useCalendar'

export { type CalendarProps }
export { selectors as calendarSelectors } from './styles'
export {
  CalendarProvider,
  type MaybeDate as MaybeCalendarDate,
} from './useCalendar'

/** @private Only exported for use in `@opallabs/kuiper` date pickers. */
export { MonthSelector } from './MonthSelector'

export const Calendar: VFC<CalendarProps> = ({
  allowedDates,
  className,
  customFooter,
  enableDateRemoval = true,
  initialFocusDate,
  initialSelectedDate,
  locale,
  onDateSelect,
  softDisallowMessage,
  weekStartsOn,
}) => {
  const theme = useTheme()

  if (enableDateRemoval && customFooter)
    throw new Error('Using customFooter requires enableDateRemoval to be false')

  return (
    <CalendarProvider
      allowedDates={allowedDates}
      initialFocusDate={initialFocusDate}
      initialSelectedDate={initialSelectedDate}
      locale={locale}
      onDateSelect={onDateSelect}
      weekStartsOn={weekStartsOn}
    >
      <div
        className={classNames(
          'lapidary-calendar',
          className,
          styles.selectors.container
        )}
        css={[styles.container, styles.dynamic(theme)]}
      >
        <Month>
          {({ isSelectedDateStrictlyAllowed }) => {
            return isSelectedDateStrictlyAllowed === false ? (
              <div css={styles.softDisallowContainer({ theme })} role="status">
                <Icon glyph="gem-warning" ariaHidden />
                {softDisallowMessage || 'Date is outside allowed date range'}
              </div>
            ) : null
          }}
        </Month>

        {customFooter && (
          <div className={styles.selectors.footer}>{customFooter}</div>
        )}
      </div>

      {enableDateRemoval && <DateRemover />}
    </CalendarProvider>
  )
}
Calendar.displayName = 'Lapidary: Calendar'
